import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, Upload, X } from "lucide-react";
import { LoadingSpinner } from "./LoadingSpinner";
import { toast } from "@/hooks/use-toast";
import { Camera as CapCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { supabase } from "@/integrations/supabase/client";
import { Analytics } from "@/lib/analytics";
import { usePremiumGate } from "@/contexts/PremiumGateContext";

interface PhotoScanModalProps {
  open: boolean;
  onClose: () => void;
  onAnalysisComplete: (detectedFoods: any[], rawResponse: string) => void;
  initialPlateDiameter?: number;
  /** When set, immediately opens camera or photo library - skips the choice step */
  initialSource?: 'camera' | 'photos';
}

export const PhotoScanModal = ({ 
  open, 
  onClose, 
  onAnalysisComplete,
  initialPlateDiameter,
  initialSource 
}: PhotoScanModalProps) => {
  const { t, i18n } = useTranslation();
  const { openPaywall } = usePremiumGate();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [plateDiameter, setPlateDiameter] = useState(initialPlateDiameter?.toString() || "");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const triggeredInitialRef = useRef(false);
  const analyzeProgressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!open) {
      triggeredInitialRef.current = false;
      return;
    }
    setPlateDiameter("");
  }, [open]);

  // When opened with initialSource, immediately open camera or gallery - skip the choice step
  useEffect(() => {
    if (open && initialSource && !imagePreview && !triggeredInitialRef.current) {
      triggeredInitialRef.current = true;
      if (initialSource === 'camera') {
        handleNativeCamera(CameraSource.Camera);
      } else {
        handleNativeCamera(CameraSource.Photos);
      }
    }
    if (!open) {
      triggeredInitialRef.current = false;
    }
  }, [open, initialSource, imagePreview]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNativeCamera = async (source: CameraSource) => {
    try {
      // Check and request appropriate permissions based on source
      const perms = await CapCamera.checkPermissions();
      
      if (source === CameraSource.Camera) {
        // Camera permission for taking photos
        if (perms.camera !== 'granted') {
          const req = await CapCamera.requestPermissions({ permissions: ['camera'] });
          if (req.camera !== 'granted') {
            const platform = Capacitor.getPlatform();
            toast({
              title: t('components.photoScan.cameraPermissionTitle'),
              description: platform === 'ios' 
                ? t('components.photoScan.cameraPermissionIos')
                : t('components.photoScan.cameraPermissionAndroid'),
              variant: 'destructive',
            });
            return;
          }
        }
      } else if (source === CameraSource.Photos) {
        // Photo library permission for uploading photos
        if (perms.photos !== 'granted') {
          const req = await CapCamera.requestPermissions({ permissions: ['photos'] });
          if (req.photos !== 'granted') {
            const platform = Capacitor.getPlatform();
            toast({
              title: t('components.photoScan.photosPermissionTitle'),
              description: platform === 'ios'
                ? t('components.photoScan.photosPermissionIos')
                : t('components.photoScan.photosPermissionAndroid'),
              variant: 'destructive',
            });
            return;
          }
        }
      }

      const photo = await CapCamera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source: source,
        // Lower capture quality for a smaller, faster upload/analysis round-trip.
        // The vision model itself is unchanged - this only reduces raw photo weight.
        quality: 70,
        allowEditing: false,
        saveToGallery: false,
        // Add these for better iPad compatibility
        correctOrientation: true,
        presentationStyle: 'fullscreen',
      });

      if (photo.dataUrl) {
        setImagePreview(photo.dataUrl);
        // Convert data URL to File object
        const response = await fetch(photo.dataUrl);
        const blob = await response.blob();
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setImageFile(file);
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast({
        title: t('components.photoScan.cameraErrorTitle'),
        description: error instanceof Error ? error.message : t('components.photoScan.cameraErrorDefault'),
        variant: 'destructive'
      });
    }
  };

  const handleCameraClick = () => {
    if (Capacitor.isNativePlatform()) {
      handleNativeCamera(CameraSource.Camera);
    } else {
      cameraInputRef.current?.click();
    }
  };

  const handleGalleryClick = () => {
    if (Capacitor.isNativePlatform()) {
      handleNativeCamera(CameraSource.Photos);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleAnalyze = async () => {
    if (!imageFile) {
      toast({
        title: t('components.photoScan.noImageTitle'),
        description: t('components.photoScan.noImageDescription'),
        variant: "destructive"
      });
      return;
    }

    // Validate image size (max 10MB)
    if (imageFile.size > 10 * 1024 * 1024) {
      toast({
        title: t('components.photoScan.imageTooLargeTitle'),
        description: t('components.photoScan.imageTooLargeDescription'),
        variant: "destructive"
      });
      return;
    }

    try {
      setIsAnalyzing(true);
      // Simulated progress so "Analyzing..." feels immediate and alive rather than a static
      // spinner - eases up near the top so it never falsely implies completion before the
      // real response arrives.
      setAnalyzeProgress(8);
      if (analyzeProgressTimerRef.current) clearInterval(analyzeProgressTimerRef.current);
      analyzeProgressTimerRef.current = setInterval(() => {
        setAnalyzeProgress((prev) => (prev >= 90 ? 90 : prev + (90 - prev) * 0.12 + 1));
      }, 350);
      Analytics.foodScanStarted();
      console.log('Starting food photo analysis...');

      const formData = new FormData();
      formData.append('image', imageFile);
      if (plateDiameter) {
        formData.append('plate_diameter_cm', plateDiameter);
      }
      formData.append('client_locale', i18n.language);
      formData.append('client_local_date', new Date().toLocaleDateString('en-CA'));

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

      try {
        // Get the user's session token
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(t('components.photoScan.loginRequired'));
        }

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-food-photo`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: formData,
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId);

        const responseData = await response.json().catch(() => ({ error: 'Unknown error' }));
        
        // Check for rate limit error (can come as 429 or in response body)
        if (response.status === 429 || responseData.error === 'daily_limit_reached') {
          Analytics.aiDailyLimitReached();
          toast({
            title: t('components.photoScan.aiLimitTitle'),
            description: responseData.message || t('premium.nutritionScanLocked'),
            variant: "destructive"
          });
          openPaywall({
            description: responseData.message || t('premium.nutritionScanLocked'),
            source: 'food_scan',
          });
          return;
        }

        if (!response.ok) {
          console.error('API error:', response.status, responseData);
          throw new Error(responseData.error || t('components.photoScan.serverError', { status: response.status }));
        }

        console.log('Analysis complete:', responseData);
        
        if (!responseData.detected_foods || responseData.detected_foods.length === 0) {
          Analytics.foodScanFailed('no_foods_detected');
          toast({
            title: t('components.photoScan.noFoodsTitle'),
            description: t('components.photoScan.noFoodsDescription'),
            variant: "destructive"
          });
          return;
        }

        Analytics.foodScanCompleted(responseData.detected_foods.length);
        onAnalysisComplete(responseData.detected_foods, responseData.raw_response);
        handleClose();
        
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          console.error('Request timeout');
          throw new Error(t('components.photoScan.analysisTimeoutMessage'));
        }
        throw fetchError;
      }
      
    } catch (error) {
      console.error('Analysis error:', error);
      Analytics.foodScanFailed(error instanceof Error ? error.message : 'unknown_error');
      toast({
        title: t('components.photoScan.analysisFailedTitle'),
        description: error instanceof Error ? error.message : t('components.photoScan.analysisFailedDefault'),
        variant: "destructive"
      });
    } finally {
      if (analyzeProgressTimerRef.current) {
        clearInterval(analyzeProgressTimerRef.current);
        analyzeProgressTimerRef.current = null;
      }
      setAnalyzeProgress(0);
      setIsAnalyzing(false);
    }
  };

  useEffect(() => {
    return () => {
      if (analyzeProgressTimerRef.current) clearInterval(analyzeProgressTimerRef.current);
    };
  }, []);

  const handleClose = () => {
    setImageFile(null);
    setImagePreview("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('components.photoScan.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!imagePreview ? (
            <div className="flex flex-col gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
              {initialSource ? (
                <p className="text-center text-muted-foreground py-8">
                  {initialSource === 'camera' ? t('components.photoScan.openingCamera') : t('components.photoScan.openingGallery')}
                </p>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="h-32"
                    onClick={handleCameraClick}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Camera className="h-8 w-8" />
                      <span>{t('components.photoScan.takePhoto')}</span>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="h-32"
                    onClick={handleGalleryClick}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-8 w-8" />
                      <span>{t('components.photoScan.uploadPhoto')}</span>
                    </div>
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden bg-muted">
                <img 
                  src={imagePreview} 
                  alt={t('components.photoScan.imgAltSelected')} 
                  className="w-full h-64 object-cover"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview("");
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="plate-diameter">
                  {t('components.photoScan.plateDiameterLabel')}
                  <span className="text-xs text-muted-foreground ml-2">
                    {t('components.photoScan.plateDiameterHint')}
                  </span>
                </Label>
                <div className="flex gap-2 items-center">
                  <Input
                    id="plate-diameter"
                    type="number"
                    placeholder={t('components.photoScan.platePlaceholder')}
                    value={plateDiameter}
                    onChange={(e) => setPlateDiameter(e.target.value)}
                    min="10"
                    max="50"
                  />
                  <span className="text-sm text-muted-foreground">{t('components.photoScan.cmShort')}</span>
                </div>
              </div>

              {isAnalyzing ? (
                <div className="relative">
                  <div className="relative rounded-lg overflow-hidden">
                    <img 
                      src={imagePreview} 
                      alt={t('components.photoScan.imgAltAnalyzing')} 
                      className="w-full h-64 object-cover opacity-30"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <LoadingSpinner size="lg" />
                    </div>
                  </div>
                  <p className="text-center text-muted-foreground mt-4 font-medium">{t('components.photoScan.analyzing')}</p>
                  <div className="mt-3 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
                      style={{ width: `${Math.min(analyzeProgress, 100)}%` }}
                    />
                  </div>
                </div>
              ) : (
                <Button 
                  onClick={handleAnalyze} 
                  className="w-full"
                >
                  {t('components.photoScan.analyzeCta')}
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
